import { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Input,
  Button,
  Drawer,
  Empty,
  Spin,
  Alert,
  Typography,
  Tooltip,
} from "antd";
import {
  MagnifyingGlass,
  ArrowClockwise,
  Graph,
  X,
} from "@phosphor-icons/react";

import {
  listGraphRuns,
  getGraphRun,
  getMatterGraphSummary,
} from "../../../service/graphRuns";

const { Title, Text, Paragraph } = Typography;

// Tunables — the same defaults the backend uses.
const DEFAULT_PAGE_SIZE = 25;

/**
 * Admin-only Graph Explorer.
 *
 * Surfaces the per-document graph-extraction audit trail captured by
 * `GraphExtractionRun` (backend PR #554). Mounted as a tab under
 * `/settings`, which is already admin-gated by `<ProtectedRoute
 * isAdminOnly>` so we don't re-check at the component level.
 *
 * Three slices of UX:
 *
 *   1. A run table with matter-id + document-id filters and DRF
 *      page-number pagination. One row per `GraphExtractionRun`,
 *      newest-first.
 *   2. A matter rollup card that appears whenever a matter filter is
 *      active, showing aggregated raw/kept/dropped counts plus the top
 *      dropped entity types and predicates. Without this you can see
 *      a single doc's drops but not "where is this matter losing the
 *      most graph signal?".
 *   3. A drawer that opens on row click and shows the inline
 *      dropped-records sample for that run — i.e. "all of the extra
 *      shit the model pulled" that didn't survive the schema gate.
 *
 * The page is self-contained — local state, no Redux slice. Matches the
 * existing admin Settings pages (`AIDashboard`, `ReferenceLibrary`,
 * `CostAnalysis`).
 */
const GraphExplorer = () => {
  // Filter inputs (committed via Apply, not on every keystroke — avoids
  // hammering the BE while the operator is still typing an id).
  const [matterIdInput, setMatterIdInput] = useState("");
  const [documentIdInput, setDocumentIdInput] = useState("");
  const [filters, setFilters] = useState({ matterId: "", documentId: "" });
  const [page, setPage] = useState(1);

  // Run list state
  const [runs, setRuns] = useState([]);
  const [runCount, setRunCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // Matter summary state (only fetched when a matter filter is committed)
  const [matterSummary, setMatterSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  // Detail drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const fetchRuns = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await listGraphRuns({
        matterId: filters.matterId || undefined,
        documentId: filters.documentId || undefined,
        page,
        pageSize: DEFAULT_PAGE_SIZE,
      });
      setRuns(res.data?.results || []);
      setRunCount(res.data?.count || 0);
    } catch (err) {
      setListError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load graph runs"
      );
      setRuns([]);
      setRunCount(0);
    } finally {
      setListLoading(false);
    }
  }, [filters, page]);

  const fetchMatterSummary = useCallback(async () => {
    if (!filters.matterId) {
      setMatterSummary(null);
      setSummaryError(null);
      return;
    }
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await getMatterGraphSummary(filters.matterId);
      setMatterSummary(res.data);
    } catch (err) {
      setSummaryError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load matter summary"
      );
      setMatterSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters.matterId]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    fetchMatterSummary();
  }, [fetchMatterSummary]);

  const applyFilters = () => {
    setPage(1);
    setFilters({
      matterId: matterIdInput.trim(),
      documentId: documentIdInput.trim(),
    });
  };

  const clearFilters = () => {
    setMatterIdInput("");
    setDocumentIdInput("");
    setPage(1);
    setFilters({ matterId: "", documentId: "" });
  };

  const openRunDrawer = async (run) => {
    setDrawerOpen(true);
    setActiveRun(run);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await getGraphRun(run.id);
      setActiveRun(res.data);
    } catch (err) {
      setDetailError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load run detail"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setActiveRun(null);
    setDetailError(null);
  };

  const columns = useMemo(
    () => [
      {
        title: "Run",
        dataIndex: "id",
        key: "id",
        width: 90,
        render: (v) => <Mono>{v}</Mono>,
      },
      {
        title: "Matter",
        dataIndex: "matter_id",
        key: "matter_id",
        width: 110,
        render: (v) => <Mono>{v ?? "—"}</Mono>,
      },
      {
        title: "Document",
        dataIndex: "document_id",
        key: "document_id",
        width: 130,
        render: (v) => <Mono>{v ?? "—"}</Mono>,
      },
      {
        title: "Model",
        dataIndex: "model_name",
        key: "model_name",
        render: (v, row) => (
          <span>
            <Text>{v || "—"}</Text>
            {row.extraction_mode ? (
              <Tag style={{ marginLeft: 8 }}>{row.extraction_mode}</Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: "Entities (kept / dropped)",
        key: "entities",
        width: 220,
        render: (_v, row) => (
          <Mono>
            {formatNumber(row.kept_entity_count)} /{" "}
            <DropEm>{formatNumber(row.dropped_entity_count)}</DropEm>
            <SubCount>raw {formatNumber(row.raw_entity_count)}</SubCount>
          </Mono>
        ),
      },
      {
        title: "Facts (kept / dropped)",
        key: "facts",
        width: 220,
        render: (_v, row) => (
          <Mono>
            {formatNumber(row.kept_fact_count)} /{" "}
            <DropEm>{formatNumber(row.dropped_fact_count)}</DropEm>
            <SubCount>raw {formatNumber(row.raw_fact_count)}</SubCount>
          </Mono>
        ),
      },
      {
        title: "When",
        dataIndex: "created_at",
        key: "created_at",
        width: 170,
        render: (v) => <Mono>{formatTimestamp(v)}</Mono>,
      },
    ],
    []
  );

  const hasMatterFilter = !!filters.matterId;

  return (
    <Wrap>
      <TopRow>
        <TopLeft>
          <Graph size={20} weight="bold" />
          <Title level={4} style={{ margin: 0 }}>
            Graph Explorer
          </Title>
          <Text type="secondary">
            Per-document graph-extraction audit trail
          </Text>
        </TopLeft>
        <Button
          icon={<ArrowClockwise size={14} />}
          onClick={() => {
            fetchRuns();
            fetchMatterSummary();
          }}
        >
          Refresh
        </Button>
      </TopRow>

      <FilterRow>
        <Input
          placeholder="Matter ID"
          value={matterIdInput}
          onChange={(e) => setMatterIdInput(e.target.value)}
          onPressEnter={applyFilters}
          allowClear
          style={{ maxWidth: 160 }}
          data-testid="graph-explorer-matter-input"
        />
        <Input
          placeholder="Document ID"
          value={documentIdInput}
          onChange={(e) => setDocumentIdInput(e.target.value)}
          onPressEnter={applyFilters}
          allowClear
          style={{ maxWidth: 180 }}
          data-testid="graph-explorer-document-input"
        />
        <Button
          type="primary"
          icon={<MagnifyingGlass size={14} />}
          onClick={applyFilters}
        >
          Apply
        </Button>
        {(filters.matterId || filters.documentId) && (
          <Button icon={<X size={14} />} onClick={clearFilters}>
            Clear
          </Button>
        )}
      </FilterRow>

      {hasMatterFilter && (
        <MatterSummarySection>
          {summaryLoading && (
            <Card>
              <Spin /> <Text type="secondary">Loading matter summary…</Text>
            </Card>
          )}
          {summaryError && !summaryLoading && (
            <Alert type="error" showIcon message={summaryError} />
          )}
          {matterSummary && !summaryLoading && (
            <MatterSummaryCard summary={matterSummary} />
          )}
        </MatterSummarySection>
      )}

      <Card
        title={
          <span>
            Runs{" "}
            <Text type="secondary" style={{ fontWeight: 400 }}>
              ({formatNumber(runCount)})
            </Text>
          </span>
        }
        bodyStyle={{ padding: 0 }}
      >
        {listError && !listLoading && (
          <Alert
            type="error"
            showIcon
            message={listError}
            style={{ margin: 16 }}
          />
        )}
        <Table
          columns={columns}
          dataSource={runs}
          rowKey="id"
          loading={listLoading}
          locale={{
            emptyText: hasMatterFilter
              ? "No graph extraction runs recorded for this matter yet."
              : "No graph extraction runs recorded yet.",
          }}
          pagination={{
            current: page,
            pageSize: DEFAULT_PAGE_SIZE,
            total: runCount,
            showSizeChanger: false,
            onChange: (next) => setPage(next),
          }}
          onRow={(row) => ({
            onClick: () => openRunDrawer(row),
            style: { cursor: "pointer" },
          })}
        />
      </Card>

      <Drawer
        title={
          activeRun ? (
            <DrawerHeader>
              <span>Run #{activeRun.id}</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                matter {activeRun.matter_id ?? "—"} · doc{" "}
                {activeRun.document_id ?? "—"} · {activeRun.model_name || "—"}
              </Text>
            </DrawerHeader>
          ) : (
            "Run detail"
          )
        }
        width={720}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
      >
        {detailLoading && !activeRun?.dropped_entities && (
          <Spin />
        )}
        {detailError && (
          <Alert type="error" showIcon message={detailError} />
        )}
        {activeRun && !detailLoading && (
          <RunDetail run={activeRun} />
        )}
      </Drawer>
    </Wrap>
  );
};

export default GraphExplorer;

/* ---------------------- helpers + subcomponents ---------------------- */

const formatNumber = (n) => {
  if (n == null) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString();
};

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString();
  } catch {
    return "—";
  }
};

const MatterSummaryCard = ({ summary }) => {
  const totals = summary || {};
  const topEntities = summary?.top_dropped_entity_types || [];
  const topPredicates = summary?.top_dropped_fact_predicates || [];

  return (
    <Card title={`Matter ${summary.matter_id} graph summary`}>
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Statistic title="Runs" value={totals.run_count || 0} />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Entities kept"
            value={totals.total_kept_entity_count || 0}
            valueStyle={{ color: "#10B981" }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Entities dropped"
            value={totals.total_dropped_entity_count || 0}
            valueStyle={{ color: "#EF4444" }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Raw entities (model)"
            value={totals.total_raw_entity_count || 0}
            valueStyle={{ color: "#64748B" }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Facts kept"
            value={totals.total_kept_fact_count || 0}
            valueStyle={{ color: "#10B981" }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Facts dropped"
            value={totals.total_dropped_fact_count || 0}
            valueStyle={{ color: "#EF4444" }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Raw facts (model)"
            value={totals.total_raw_fact_count || 0}
            valueStyle={{ color: "#64748B" }}
          />
        </Col>
        <Col xs={12} sm={6}>
          <Statistic
            title="Ignored entity types"
            value={totals.total_dropped_ignored_entity_count || 0}
            valueStyle={{ color: "#94A3B8" }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Title level={5}>Top dropped entity types</Title>
          {topEntities.length === 0 ? (
            <Empty
              description="No dropped entity types"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <TopList data-testid="top-dropped-entity-types">
              {topEntities.map((row) => (
                <li key={row.type}>
                  <code>{row.type || "(unknown)"}</code>
                  <Tag color="red">{formatNumber(row.row_count)} runs</Tag>
                </li>
              ))}
            </TopList>
          )}
        </Col>
        <Col xs={24} md={12}>
          <Title level={5}>Top dropped fact predicates</Title>
          {topPredicates.length === 0 ? (
            <Empty
              description="No dropped predicates"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <TopList data-testid="top-dropped-predicates">
              {topPredicates.map((row) => (
                <li key={row.predicate}>
                  <code>{row.predicate || "(unknown)"}</code>
                  <Tag color="red">{formatNumber(row.row_count)} runs</Tag>
                </li>
              ))}
            </TopList>
          )}
        </Col>
      </Row>
    </Card>
  );
};

const RunDetail = ({ run }) => {
  const entities = run.dropped_entities || [];
  const facts = run.dropped_facts || [];
  const droppedEntityTypes = run.dropped_entity_types || [];
  const droppedPredicates = run.dropped_fact_predicates || [];
  const droppedIgnored = run.dropped_ignored_entity_types || [];

  return (
    <DetailWrap>
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={8}>
          <Statistic
            title="Chunks"
            value={run.chunk_count || 0}
          />
        </Col>
        <Col xs={12} sm={8}>
          <Statistic
            title="Entities kept / dropped"
            value={`${formatNumber(run.kept_entity_count)} / ${formatNumber(
              run.dropped_entity_count
            )}`}
          />
        </Col>
        <Col xs={12} sm={8}>
          <Statistic
            title="Facts kept / dropped"
            value={`${formatNumber(run.kept_fact_count)} / ${formatNumber(
              run.dropped_fact_count
            )}`}
          />
        </Col>
      </Row>

      <ArtifactRow>
        <Text strong>Artifacts:</Text>{" "}
        {run.graph_artifact_uri ? (
          <Tooltip title={run.graph_artifact_uri}>
            <code>{shortenUri(run.graph_artifact_uri)}</code>
          </Tooltip>
        ) : (
          <Text type="secondary">graph_json: —</Text>
        )}{" "}
        |{" "}
        {run.raw_graph_artifact_uri ? (
          <Tooltip title={run.raw_graph_artifact_uri}>
            <code>raw: {shortenUri(run.raw_graph_artifact_uri)}</code>
          </Tooltip>
        ) : (
          <Text type="secondary">raw: —</Text>
        )}
      </ArtifactRow>

      {droppedEntityTypes.length > 0 && (
        <DetailSection>
          <Title level={5}>Dropped entity types ({droppedEntityTypes.length})</Title>
          <TagWrap>
            {droppedEntityTypes.map((t) => (
              <Tag key={t} color="red">
                {t}
              </Tag>
            ))}
          </TagWrap>
        </DetailSection>
      )}

      {droppedPredicates.length > 0 && (
        <DetailSection>
          <Title level={5}>Dropped fact predicates ({droppedPredicates.length})</Title>
          <TagWrap>
            {droppedPredicates.map((p) => (
              <Tag key={p} color="red">
                {p}
              </Tag>
            ))}
          </TagWrap>
        </DetailSection>
      )}

      {droppedIgnored.length > 0 && (
        <DetailSection>
          <Title level={5}>
            Ignored entity types ({droppedIgnored.length})
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            These types are explicitly skipped by the sanitizer (e.g.
            <code> Claimant</code>, <code>Veteran</code>) — surfaced here so
            you can confirm the model is still emitting them.
          </Paragraph>
          <TagWrap>
            {droppedIgnored.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </TagWrap>
        </DetailSection>
      )}

      <DetailSection data-testid="dropped-entities-section">
        <Title level={5}>
          Dropped entities sample{" "}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            ({entities.length})
          </Text>
        </Title>
        {entities.length === 0 ? (
          <Empty description="No dropped entities" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <SamplePre>{JSON.stringify(entities, null, 2)}</SamplePre>
        )}
      </DetailSection>

      <DetailSection data-testid="dropped-facts-section">
        <Title level={5}>
          Dropped facts sample{" "}
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            ({facts.length})
          </Text>
        </Title>
        {facts.length === 0 ? (
          <Empty description="No dropped facts" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <SamplePre>{JSON.stringify(facts, null, 2)}</SamplePre>
        )}
      </DetailSection>
    </DetailWrap>
  );
};

const shortenUri = (uri) => {
  if (!uri) return "—";
  if (uri.length <= 60) return uri;
  return `${uri.slice(0, 20)}…${uri.slice(-30)}`;
};

/* ---------------------- styled ---------------------- */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0 32px;
  font-family: Manrope, sans-serif;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const MatterSummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Mono = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: 12px;
`;

const DropEm = styled.span`
  color: #b91c1c;
  font-weight: 600;
`;

const SubCount = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const DrawerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ArtifactRow = styled.div`
  font-size: 12px;
  color: #475569;
  code {
    font-family: "SF Mono", "Fira Code", monospace;
    font-size: 11px;
    background: #f1f5f9;
    padding: 1px 6px;
    border-radius: 4px;
  }
`;

const TagWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SamplePre = styled.pre`
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 11px;
  line-height: 1.5;
  font-family: "SF Mono", "Fira Code", monospace;
  max-height: 360px;
  overflow: auto;
  margin: 0;
`;

const TopList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 6px 10px;
  }
  code {
    font-family: "SF Mono", "Fira Code", monospace;
    font-size: 12px;
    color: #0f172a;
  }
`;
